import { fetchWithdrawal } from 'actions/SettlementActions';
import { Input } from 'components/custom/input';
import { useTranslate } from 'context/TranslateContext';
import { useIdempotencyKey } from 'helpers/hooks/useIdempotencyKey';
import { ChangeEvent, FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { FunctionDispatch, GlobalTypes } from 'redux/types';
import { useSwal } from 'helpers/sweetalert';
import { validate } from './validator';
import { convertCurrenciesOptions } from 'helpers/hooks/useCurrency';
import currencies from 'constants/Currencies';

export interface WithdrawalModalProps {
  isOpenModal: boolean;
  setIsOpenModal: (state: boolean) => void;
  reloadData?: () => Promise<void>;
}

export const WithdrawalModal: FC<WithdrawalModalProps> = ({
  isOpenModal,
  setIsOpenModal,
  reloadData,
}: WithdrawalModalProps) => {
  const {
    auth: { user },
  } = useSelector((state: GlobalTypes.RootState) => state);
  const settlementCurrencies = useMemo(() => {
    const currencies = user?.balance_info_list?.data?.map((item) => item.currency) || [];

    return currencies.length > 0 ? currencies : ['HKD'];
  }, [user]);

  const initialState = {
    amount: '',
    currency: '',
  };

  const [data, setData] = useState(initialState);
  const [addIdempotencyKey, resetIdempotencyKey] = useIdempotencyKey<typeof data>();
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const { translate, language } = useTranslate();
  const [Swal] = useSwal();

  const dispatch = useDispatch<FunctionDispatch>();

  const { amount, currency } = data;

  const handleInputChange = (e: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setData({
      ...data,
      [e.target.name]: (e.target as any).rawValue,
    });
  };

  const handleSelectChange = (name: string) => (value: string) => {
    setData({
      ...data,
      [name]: value,
    });
    // Clear the error for the field when the value changes
    setErrors({});
  };

  const handleCloseModal = () => {
    if (reloadData) {
      reloadData();
    }
    setIsOpenModal(false);
    setData(initialState);
    setErrors({});
  };

  const onSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { isValid, errors } = validate(data, language);
    setErrors(errors);
    if (!isValid) {
      return;
    }
    try {
      setIsLoading(true);
      const res = await dispatch(fetchWithdrawal(data));
      await Swal.fire({
        icon: res.success ? 'success' : 'error',
        title: translate('withdrawal'),
        text: res.message,
      });
    } finally {
      setIsLoading(false);
    }

    handleCloseModal();
  };

  useEffect(resetIdempotencyKey, [isOpenModal]);

  return (
    <Modal
      isOpen={isOpenModal}
      toggle={handleCloseModal}
      unmountOnClose={true}
      className="round-lg"
      centered={true}>
      <ModalHeader toggle={handleCloseModal}>{translate('withdrawal')}</ModalHeader>
      <ModalBody>
        <Input
          legend={translate('currency').toUpperCase()}
          name="currency"
          onChange={handleSelectChange('currency')}
          value={currency}
          type="react-select"
          defaultLabel={translate('select')}
          placeholder={translate('select')}
          options={convertCurrenciesOptions(settlementCurrencies,translate)}
          error={translate(...(errors.currency ?? ''))}
        />
        <Input
          legend={translate('amount').toUpperCase()}
          name="amount"
          onChange={handleInputChange}
          value={amount}
          type="cleave"
          placeholder={translate('enter_amount')}
          prefix="$"
          cleaveOptions={{
            numeral: true,
            numeralDecimalScale: 2,
            numeralIntegerScale: 7,
            numeralPositiveOnly: true,
          }}
          error={translate(...(errors.amount ?? ''))}
        />

        {currency === currencies.USD.value && (
          <div className="ps-2 pt-1" style={{ color: '#c84744' }}>
            *{translate('settlement_fee_remark_message', ['8'])}
          </div>
        )}

        <div className="d-grid">
          <button className="btn btn-primary mt-4 round-lg" disabled={isLoading} onClick={onSubmit}>
            {isLoading ? <Spinner size="sm" /> : translate('send')}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};
